import appendQuery from 'append-query';
import 'url-search-params-polyfill';
import environment from 'platform/utilities/environment';
import {
  createOAuthRequest,
  updateStateAndVerifier,
} from 'platform/utilities/oauth/utilities';
import { setLoginAttempted } from 'platform/utilities/sso/loginAttempted';
import { externalApplicationsConfig } from './usip-config';
import {
  AUTH_EVENTS,
  AUTHN_SETTINGS,
  API_VERSION,
  EXTERNAL_APPS,
  EXTERNAL_REDIRECTS,
  GA,
  CSP_IDS,
  POLICY_TYPES,
  SIGNUP_TYPES,
  API_SESSION_URL,
  AUTH_PARAMS,
  IDME_TYPES,
  AUTH_SSO_APPS,
} from './constants';
import recordEvent from '../../monitoring/record-event';

// NOTE: the login app typically has URLs that being with 'sign-in',
// however there is at least one CMS page, 'sign-in-faq', that we don't
// want to resolve with the login app
export const loginAppUrlRE = new RegExp('^/sign-in(/.*)?$');

export const getQueryParams = () => {
  return Object.keys(AUTH_PARAMS).reduce((paramsObj, paramKey) => {
    const paramValue = new URLSearchParams(window.location.search).get(
      AUTH_PARAMS[paramKey],
    );
    return { ...paramsObj, ...(paramValue && { [paramKey]: paramValue }) };
  }, {});
};

export const reduceProviders = obj =>
  Object.entries(obj).reduce((acc, [key, value]) => {
    if (value) acc.push(key);
    return acc;
  }, []);

export const reduceAllowedProviders = (obj, type) => {
  if (!type || type === '') return reduceProviders(obj);

  if (
    Object.keys(obj).includes('registeredApps') &&
    type === 'registeredApps'
  ) {
    return reduceProviders(obj.registeredApps);
  }

  if (Object.keys(obj).includes('default') && type === 'default') {
    return reduceProviders(obj.default);
  }

  return reduceProviders(obj);
};

export const isExternalRedirect = () => {
  const { application } = getQueryParams();
  return (
    loginAppUrlRE.test(window.location.pathname) &&
    Object.keys(EXTERNAL_REDIRECTS).includes(application)
  );
};

export const sanitizeUrl = (url, path = '') => {
  if (!url) return null;
  const updatedUrl = url.endsWith('/') ? url.slice(0, -1) : url;
  const updatedPath = path ?? '';
  return `${updatedUrl}${updatedPath}`.replace('\r\n', ''); // Prevent CRLF injection.
};

export const sanitizePath = to => {
  if (!to) return '';
  return to.startsWith('/') ? to : `/${to}`;
};

export const sanitizeCernerParams = path => {
  if (!path) return '/?authenticated=true';
  const [updatedPath] = decodeURIComponent(path).split('?');
  return `${updatedPath}?authenticated=true`;
};

export const sanitizeOracleHealth = ({ application }) => {
  const { externalRedirectUrl, alternateRedirectUrl } =
    externalApplicationsConfig[application];

  const startingURL = new URL(window.location);
  // return early if no `to` query param
  if (!startingURL?.searchParams?.has('to')) {
    return sanitizeUrl(externalRedirectUrl, sanitizeCernerParams());
  }

  const [_nestedPath, _nestedTo] = decodeURIComponent(
    startingURL?.searchParams?.get('to'),
  ).split('?');

  const updatedStartingURL = _nestedTo?.includes(alternateRedirectUrl)
    ? alternateRedirectUrl
    : externalRedirectUrl;

  return sanitizeUrl(
    `${updatedStartingURL}`,
    sanitizeCernerParams(_nestedPath),
  );
};

export const generateReturnURL = returnUrl => {
  return [
    ``, // create account links don't have a authReturnUrl
    `${environment.BASE_URL}/?next=loginModal`,
    `${environment.BASE_URL}`,
  ].includes(returnUrl)
    ? `${environment.BASE_URL}/my-va/`
    : returnUrl;
};

export const createExternalApplicationUrl = () => {
  const { application, to } = getQueryParams();
  if (!application) {
    return null;
  }
  const { externalRedirectUrl } = externalApplicationsConfig[application];
  let URL = '';

  switch (application) {
    case EXTERNAL_APPS.VA_FLAGSHIP_MOBILE:
      URL = externalRedirectUrl;
      break;
    case EXTERNAL_APPS.VA_OCC_MOBILE:
      URL = sanitizeUrl(`${externalRedirectUrl}${window.location.search}`);
      break;
    case EXTERNAL_APPS.MHV:
      URL = sanitizeUrl(
        `${externalRedirectUrl}`,
        `${!to ? '' : `?deeplinking=${to}`}`,
      );
      break;
    case EXTERNAL_APPS.MY_VA_HEALTH:
      URL = externalRedirectUrl;
      break;
    case EXTERNAL_APPS.ARP:
      URL = sanitizeUrl(externalRedirectUrl, to);
      break;
    case EXTERNAL_APPS.SMHD:
      URL = `${sanitizeUrl(`${externalRedirectUrl}`)}/`;
      break;
    default:
      break;
  }
  return URL;
};

export const getGAClientId = () => {
  try {
    // eslint-disable-next-line no-undef
    const trackers = ga.getAll();

    const tracker = trackers.find(t => {
      const trackingId = t.get(GA.trackingIdKey);
      return GA.trackingIds.includes(trackingId);
    });

    return (tracker && tracker.get(GA.clientIdKey)) ?? null;
  } catch (e) {
    return null;
  }
};

// Return URL is where a user will be forwarded post successful authentication
export const createAndStoreReturnUrl = () => {
  let returnUrl;
  if (loginAppUrlRE.test(window.location.pathname)) {
    if (isExternalRedirect()) {
      returnUrl = createExternalApplicationUrl();
    } else {
      // Preserve a pre-set return URL (e.g., from MHV→VA.gov OAuth redirect)
      const presetReturnUrl = sessionStorage.getItem(AUTHN_SETTINGS.RETURN_URL);
      if (presetReturnUrl && presetReturnUrl !== window.location.origin) {
        return presetReturnUrl;
      }
      // Return user to home page if internal authentication via USiP
      returnUrl = window.location.origin;
    }
  } else {
    if (sessionStorage.getItem(AUTHN_SETTINGS.RETURN_URL)) {
      return sessionStorage.getItem(AUTHN_SETTINGS.RETURN_URL);
    }
    // If we are not on the USiP, we should always return the user back to their current location
    returnUrl = window.location.toString();
  }
  if (window.location.pathname === '/verify/') {
    returnUrl = '/my-va';
  }

  sessionStorage.setItem(
    AUTHN_SETTINGS.RETURN_URL,
    returnUrl.replace(/&oauth=(true|false)/, ''),
  );
  return returnUrl;
};

export function sessionTypeUrl({
  type = '',
  queryParams = {},
  version = API_VERSION,
  allowVerification = false,
  useOauth = null,
  acr = null,
  clientId: passedClientId = null,
}) {
  if (!type) {
    return null;
  }

  // application is fetched from location, not the passed through queryParams arg
  const {
    application,
    OAuth = true,
    codeChallenge,
    codeChallengeMethod,
    clientId: urlClientId,
    scope,
    state,
    verification: forceVerify,
    authorizeSsoId,
  } = getQueryParams();

  const clientId = passedClientId || urlClientId;

  const externalRedirect = isExternalRedirect();
  const isSignup = Object.values(SIGNUP_TYPES).includes(type);
  const isLogin = Object.values(CSP_IDS).includes(type);
  const config =
    externalApplicationsConfig[application] ||
    externalApplicationsConfig.default;

  // We should use OAuth when the following are true:
  // OAuth param is 'true'
  // config.OAuthEnabled is true
  const useOAuth =
    useOauth === true ||
    (useOauth !== false && config?.OAuthEnabled && OAuth === 'true');

  // Only require verification when all of the following are true:
  // 1. On the USiP (Unified Sign In Page)
  // 2. The outbound application is one of the mobile apps
  // 3. The generated link type is for signup, and login only
  const requireVerification =
    type !== POLICY_TYPES.SLO &&
    (allowVerification ||
      forceVerify === 'required' ||
      (externalRedirect &&
        (isLogin || isSignup) &&
        config.requiresVerification))
      ? '_verified'
      : '';

  // Passes GA Client ID if it is an `ID.me` type
  const gaClientId = IDME_TYPES.includes(type) && getGAClientId();

  const appendParams =
    externalRedirect && isLogin
      ? {
          ...(config.queryParams.allowPostLogin && { postLogin: true }),
          ...(config.queryParams.allowRedirect && {
            redirect: createExternalApplicationUrl(),
          }),
        }
      : {};

  if (useOAuth && (isLogin || isSignup)) {
    return createOAuthRequest({
      acr,
      application,
      clientId,
      type,
      config,
      passedQueryParams: {
        codeChallenge,
        codeChallengeMethod,
        ...(gaClientId && { gaClientId }),
        ...(scope && { scope }),
        ...(authorizeSsoId && { authorizeSsoId }),
        ...(state && { state }),
        ...(queryParams.operation && { operation: queryParams.operation }),
      },
      passedOptions: {
        isSignup,
        forceVerify,
      },
    });
  }
  return appendQuery(
    API_SESSION_URL({
      version,
      type: `${type}${requireVerification}`,
    }),
    {
      ...queryParams,
      ...appendParams,
      ...(gaClientId && {
        [GA.queryParams.default]: gaClientId,
      }),
      application,
    },
  );
}

export function redirect(redirectUrl, clickedEvent, type = '') {
  const { application } = getQueryParams();
  const externalRedirect = isExternalRedirect();
  const existingReturnUrl = sessionStorage.getItem(AUTHN_SETTINGS.RETURN_URL);

  // Keep track of the URL to return to after auth operation.
  // If the user is coming via the standalone sign-in, redirect to the home page.
  // Do not overwite an existing returnUrl for VERIFY attempts
  if (
    !(existingReturnUrl && clickedEvent === AUTH_EVENTS.VERIFY) &&
    application !== CSP_IDS.MHV
  ) {
    createAndStoreReturnUrl();
  }

  recordEvent({ event: type ? `${clickedEvent}-${type}` : clickedEvent });

  // Trigger USiP External Auth Event
  if (
    externalRedirect &&
    [AUTH_EVENTS.SSO_LOGIN, AUTH_EVENTS.MODAL_LOGIN].includes(clickedEvent)
  ) {
    recordEvent({
      event: `${AUTHN_SETTINGS.REDIRECT_EVENT}-${application}-inbound`,
    });
  }

  window.location = redirectUrl;
}

export const SMART_CARD_CSP_TYPE = 'entra';
export const SMART_CARD_ACR = 'ial2';

const CLEAR_EXCLUDED_APPS = [
  EXTERNAL_APPS.ARP,
  EXTERNAL_APPS.MHV,
  EXTERNAL_APPS.VA_OCC_MOBILE,
];

export const isClearEligible = ({ clientId, externalApplication } = {}) =>
  !AUTH_SSO_APPS.includes(clientId) &&
  !CLEAR_EXCLUDED_APPS.includes(externalApplication);

export async function signInWithSmartCard() {
  const url = await createOAuthRequest({
    application: EXTERNAL_APPS.ARP,
    type: SMART_CARD_CSP_TYPE,
    acr: SMART_CARD_ACR,
  });
  // Flag the PIV/entra flow for the ARP OAuth callback. Use a dedicated,
  // single-use key instead of the shared serviceName that platform token
  // refresh reads, so an abandoned attempt can't mislabel a later sign-in.
  sessionStorage.setItem('arpSignInCsp', SMART_CARD_CSP_TYPE);
  if (!isExternalRedirect()) {
    setLoginAttempted();
  }
  return redirect(url, AUTH_EVENTS.MODAL_LOGIN);
}

export async function mockLogin({
  clickedEvent = AUTH_EVENTS.MOCK_LOGIN,
  type = '',
} = {}) {
  if (!type) {
    throw new Error('Attempted to call mockLogin without a type');
  }
  const url = await createOAuthRequest({
    clientId: 'vamock',
    type,
  });

  return redirect(url, clickedEvent);
}

export async function login({
  policy,
  version = API_VERSION,
  queryParams = {},
  clientId = null,
  clickedEvent = AUTH_EVENTS.MODAL_LOGIN,
  isLink = false,
}) {
  // Clear any PIV/entra flag from an abandoned smart card attempt so this
  // non-PIV sign-in isn't mislabeled by the ARP callback.
  sessionStorage.removeItem('arpSignInCsp');
  const url = await sessionTypeUrl({
    type: policy,
    version,
    queryParams,
    clientId,
  });
  if (!isExternalRedirect()) {
    setLoginAttempted();
  }
  return isLink ? url : redirect(url, clickedEvent);
}

export function mfa(version = API_VERSION) {
  return redirect(
    sessionTypeUrl({ type: POLICY_TYPES.MFA, version }),
    AUTH_EVENTS.MFA,
  );
}

export async function verify({
  policy,
  version = API_VERSION,
  clickedEvent = AUTH_EVENTS.VERIFY,
  isLink = false,
  useOAuth = true,
  acr = null,
  queryParams = {},
}) {
  if (!policy) {
    throw new Error('`policy` must be provided');
  }
  const type = SIGNUP_TYPES[policy];
  const url = await sessionTypeUrl({
    type,
    version,
    useOauth: useOAuth,
    ...(!useOAuth && { allowVerification: true }),
    acr,
    queryParams,
  });

  return isLink ? url : redirect(url, `${type}-${clickedEvent}`);
}

export function logout({
  version = API_VERSION,
  clickedEvent = AUTH_EVENTS.LOGOUT,
  queryParams = {},
} = {}) {
  return redirect(
    sessionTypeUrl({ type: POLICY_TYPES.SLO, version, queryParams }),
    clickedEvent,
  );
}

export async function signupOrVerify({
  version = API_VERSION,
  policy = '',
  isSignup = true,
  isLink = false,
  useOAuth = true,
  allowVerification = true,
  config = 'default',
}) {
  const type = SIGNUP_TYPES[policy];
  const url = await sessionTypeUrl({
    type,
    version,
    ...(useOAuth && {
      // acr determined by signup or verify
      acr: isSignup
        ? externalApplicationsConfig[config].oAuthOptions.acrSignup[type]
        : externalApplicationsConfig[config].oAuthOptions.acrVerify[policy],
      useOauth: useOAuth,
    }),
    // just verify (<csp>_signup_verified)
    ...(!isSignup &&
      !useOAuth && {
        allowVerification,
      }),
    // just signup
    ...(isSignup &&
      policy === CSP_IDS.ID_ME && { queryParams: { operation: 'sign_up' } }),
  });
  const eventBase = isSignup ? AUTH_EVENTS.REGISTER : AUTH_EVENTS.VERIFY;
  const eventAuthBroker = useOAuth ? 'sis' : 'iam';
  const eventIsVerified = allowVerification ? '-verified' : '';
  const event = `${policy}-${eventBase}${eventAuthBroker}${eventIsVerified}`;

  if (isLink) {
    return url;
  }

  // Links promote the <csp>_signup state/verifier on click; direct redirects
  // must do it here so the callback's state check passes.
  if (useOAuth) {
    updateStateAndVerifier(policy);
  }

  return redirect(url, event);
}

export const logoutUrl = () => {
  return sessionTypeUrl({ type: POLICY_TYPES.SLO, version: API_VERSION });
};

// Sanitizes an untrusted redirect target so it can only point to a same-origin
// path. Returns a safe, origin-relative path. Guards against open redirects including
// protocol-relative URLs (`//external.com`), scheme URLs (`https://`, `javascript:`),
// backslash variants browsers normalize to `/` (`/\external.com`), and tab/newline tricks.
export const sanitizeReturnUrl = rawParam => {
  if (!rawParam) return null;
  // Normalize characters browsers collapse or ignore when resolving a URL:
  // backslashes become `/`, and tab/newline/CR are stripped. This prevents
  // bypasses like `/\external.com` or `/\t/external.com` that would resolve off-site.
  const normalized = rawParam
    .replace(/\\/g, '/')
    .replace(/[\t\n\r]/g, '')
    .trim();
  // Reject anything with an explicit scheme (http:, javascript:, data:, etc.)
  if (/^[a-z][a-z0-9+.-]*:/i.test(normalized)) return null;
  const path = normalized.startsWith('/') ? normalized : `/${normalized}`;
  // Reject protocol-relative URLs (//external.com) that redirect off-site.
  if (path.startsWith('//')) return null;
  return path;
};

// Reads the user's intended destination from URL params or existing sessionStorage,
// saves it to PASSKEY_PRIOR_RETURN_URL for later use, and returns it.
// Priority: ?next > ?to > existing authReturnUrl > fallback
export const captureIntendedReturnUrl = (fallback = '/my-va') => {
  const params = new URLSearchParams(window.location.search);

  const toSafeUrl = rawParam => {
    const path = sanitizeReturnUrl(rawParam);
    return path ? `${window.location.origin}${path}` : null;
  };

  const url =
    toSafeUrl(
      params.get('next') !== 'loginModal' ? params.get('next') : null,
    ) ||
    toSafeUrl(params.get('to')) ||
    sessionStorage.getItem(AUTHN_SETTINGS.RETURN_URL) ||
    fallback;

  sessionStorage.setItem(AUTHN_SETTINGS.PASSKEY_PRIOR_RETURN_URL, url);
  return url;
};
